import { Button, Divider, Drawer, Space, Spin, Table, Typography } from "antd";
import { MeasurementTank, WarehouseInventoryControl } from "api/generated";
import { useDistributionPoints } from "hooks/distributionPoints/useDistributionPoints";
import {
  DailyStockControlData,
  getContainerStockColumns,
  getStockControlColumns,
  getWarehouseStockColumns,
} from "./StockControlColumns";
import React, { useEffect, useState, Fragment } from "react";

interface InventoryControlDetailsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  record: DailyStockControlData | undefined;
  getLastMeasurement: (
    wareHouseInventoryControlId: string
  ) => Promise<MeasurementTank[]>;
}

function InventoryControlDetails({
  isOpen,
  onClose,
  record,
  getLastMeasurement,
}: InventoryControlDetailsDrawerProps) {
  const { distributionPoint } = useDistributionPoints();
  const columnActions = {
    showActions: false,
  };
  const gicColumns = getStockControlColumns(columnActions);
  const wicColumns = getWarehouseStockColumns();
  const containerColumns = getContainerStockColumns();

  const [measurementsByTank, setMeasurementsByTank] = useState<
    MeasurementTank[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!record || !isOpen) {
      setMeasurementsByTank([]);
      return;
    }

    const fetchMeasurements = async () => {
      setIsLoading(true);
      try {
        const allMeasurements = await Promise.all(
          record.wic.map((wic) =>
            getLastMeasurement(wic.idWarehouseInventoryControl ?? "")
          )
        );
        setMeasurementsByTank(allMeasurements.flat());
      } catch (error) {
        console.error("Error fetching measurements:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMeasurements();
  }, [record?.id, isOpen]);

  return (
    <Drawer
      title={`Detalles de control de existencias para el dia:
         ${record?.gicDate.format("DD MMM YYYY")}`}
      height="auto"
      placement="bottom"
      onClose={onClose}
      open={isOpen}
      styles={{
        mask: {
          backdropFilter: "blur(10px)",
        },
        wrapper: {
          minWidth: "-webkit-fill-available",
        },
      }}
      footer={
        <Space>
          <Button onClick={onClose} className="back-button">
            Cerrar
          </Button>
        </Space>
      }
    >
      {record && (
        <Fragment>
          <Typography.Title>{distributionPoint?.name}</Typography.Title>

          <Divider style={{ borderColor: "#7cb305" }}>
            Control de existencias general
          </Divider>

          <Table<DailyStockControlData>
            columns={gicColumns}
            dataSource={[record]}
            scroll={{ x: "max-content" }}
            pagination={false}
          />

          <Divider style={{ borderColor: "#46a0d4ff" }}>
            Control de existencias por almacén
          </Divider>
          <Table<WarehouseInventoryControl>
            columns={wicColumns}
            dataSource={record.wic}
            scroll={{ x: "max-content" }}
            pagination={false}
          />

          <Divider style={{ borderColor: "#1ebe46ff" }}>
            Mediciones de volumen por tanque
          </Divider>

          <Spin spinning={isLoading}>
            <Table<MeasurementTank>
              columns={containerColumns}
              dataSource={measurementsByTank}
              scroll={{ x: "max-content", y: 55 * 3 }}
              pagination={false}
            />
          </Spin>
        </Fragment>
      )}
    </Drawer>
  );
}

export default React.memo(InventoryControlDetails);
